/** Title-case a lead enum value (e.g. "MEDIUM" → "Medium") for display. */
export function formatPriority(priority: string | null | undefined): string {
  if (!priority) return '';
  return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
}

/**
 * Tailwind background class for a lead priority's status dot. Shared by the
 * kanban card, the table's priority cell, and the detail meta line so the
 * priority color language stays identical across every leads surface.
 */
export const LEAD_PRIORITY_DOT: Record<string, string> = {
  URGENT: 'bg-red-500',
  HIGH: 'bg-orange-400',
  MEDIUM: 'bg-slate-300',
  LOW: 'bg-slate-200',
};

export function leadPriorityDotClass(priority: string | null | undefined): string {
  return (priority && LEAD_PRIORITY_DOT[priority]) || 'bg-slate-300';
}

/** Title-case a lead source (e.g. "WEB_FORM" → "Web Form") for display. */
export function formatSource(source: string | null | undefined): string {
  if (!source) return '';
  return source
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Suite F (F-03): a lead's positive terminal is CONVERTED — leads have no
 * "Won", that concept lives on estimates. Every outcome surface keys off LOST,
 * the one value the rename didn't touch, so a converted lead (and any legacy
 * 'WON' row on a database that predates the backfill) still reads as positive.
 */
export function isLostOutcome(outcome: string | null | undefined): boolean {
  return outcome === 'LOST';
}

/**
 * True when an outcome should render as a win.
 *
 * Ask this — not `!isLostOutcome(...)` — anywhere the answer drives a positive
 * treatment. `isLostOutcome` is a negative list, so it calls every value it has
 * not heard of positive: when U-TERM1 added DISQUALIFIED, a lead binned as spam
 * rendered with a success tone, a trophy, and "Converted — no further action
 * needed." A positive list cannot fail that way; a new terminal is simply not
 * a win until someone says so.
 *
 * Legacy 'WON' counts. Rows written before the WON -> CONVERTED backfill are
 * still served to this UI and are genuine conversions.
 *
 * Named to avoid `isConvertedOutcome`, which operations exports with a
 * different contract — strict equality to CONVERTED, so legacy 'WON' is false
 * there. That is correct for analytics over normalized rows and wrong for
 * rendering, which is why these must not share a name.
 */
export function isWinningOutcome(outcome: string | null | undefined): boolean {
  return outcome === 'CONVERTED' || outcome === 'WON';
}

export interface LeadValueFact {
  label: string;
  amount: number | null;
}

/**
 * Which money a lead should show, and what to call it.
 *
 * A lead carries two amounts and they mean different things. `estimatedValue`
 * is the opening figure captured at intake; it seeds the first line item when
 * the lead converts to an estimate and is never re-synced afterwards, so it
 * goes stale the moment the quote is edited or taxed. `actualValue` is the
 * approved estimate's grand total, stamped once at approval.
 *
 * A converted lead must therefore read from `actualValue`: `estimatedValue` is
 * usually null there (nobody guesses at intake), which rendered the tile as an
 * em dash while the real revenue sat in the same API response — and where it is
 * populated it names a figure the deal did not close at. Reports have always
 * read `actualValue` for revenue, so the two disagreed.
 *
 * The fallback covers a lead converted without a quote — booked straight to a
 * job, no estimate — where the opening number is the only one there is.
 */
export function resolveLeadValueFact(lead: {
  outcome?: string | null;
  estimatedValue?: number | null;
  actualValue?: number | null;
}): LeadValueFact {
  if (isWinningOutcome(lead.outcome)) {
    return {
      label: 'Converted value',
      amount: toAmount(lead.actualValue ?? lead.estimatedValue),
    };
  }
  return { label: 'Est. value', amount: toAmount(lead.estimatedValue) };
}

/**
 * Both columns are `Decimal` in Prisma, which serializes to a JSON **string** —
 * so the `number | null` on the Lead type is an assertion the wire does not
 * honour. Callers that format through `Intl` coerce internally and never
 * noticed; a caller that reaches for `.toLocaleString()` directly gets
 * `String.prototype`'s version, which is a no-op, and silently drops the
 * thousands separator ("$5200"). Normalising here fixes every call site at
 * once rather than asking each one to remember.
 */
function toAmount(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

interface NameableUser {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
}

/**
 * Resolve a team user to a human name, falling back to email then a label.
 * Shared by the lead table, detail page, and assignment timeline so they all
 * render names (never raw UUIDs).
 */
export function formatUserName(
  user: NameableUser | undefined | null,
  fallback = 'Unassigned',
): string {
  if (!user) return fallback;
  return (
    (
      user.displayName ??
      [user.firstName, user.lastName].filter(Boolean).join(' ') ??
      user.email ??
      ''
    ).trim() ||
    user.email ||
    fallback
  );
}
